import { Form } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { getActivateOrderPageKey, getOrderItemPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { Chat } from 'components/ui'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Button, Flex, TextArea, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { DATE_FORMAT_WITH_TIME } from 'shared/constants/values'

import { ticketFileValidate, TicketKeys } from '../../../data'
import { TicketStatus, TicketSupportLogsDTO } from '../../../types'

import SendMessageIcon from 'assets/img/searchEngine/send-message__icon.svg?react'

type TicketChatProps = {
  supportLogs: TicketSupportLogsDTO[]
  status: TicketStatus
}

const getKey = (key: string) => `${TicketKeys.ticketItemPage}.${key}`

export const TicketChat = observer(({ supportLogs, status }: TicketChatProps) => {
  const { viewTicketUiStore: uiStore } = useStore()

  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const sendMessageButton = async () => {
    setLoading(true)
    const result = await uiStore.createSupportLog()
    setLoading(false)

    if (result) {
      uiStore.reset()
    }
  }

  const deleteFileHandler = (index: number) => {
    const newFiles = [...uiStore.files]
    newFiles.splice(index, 1)
    uiStore.setFiles(newFiles)
  }

  return (
    <Flex vertical gap={16}>
      {supportLogs.length > 0 && (
        <>
          <Title level={5}>{t(getOrderItemPageKey('Переписка с техподдержкой'))}</Title>

          <Chat
            messages={supportLogs.map((log) => ({
              text: log.message,
              photos: [...(log.imageUrls ?? []), ...(log.documentUrls ?? [])],
              videos: log.videoUrls ?? [],
              me: !log.isManager,
              name: log.fio,
              time: dayjs(log.createDate).format(DATE_FORMAT_WITH_TIME),
              date: new Date(log.createDate),
            }))}
          />
        </>
      )}

      {status !== TicketStatus.closed && (
        <Form layout="vertical">
          <Form.Item
            label={`${t(getSharedKey('attachFiles'))} (${t(getSharedKey('notRequired'))})`}
            tooltip={t(getKey('При необходимости, загрузите файлы'))}
          >
            <Flex gap={8} wrap>
              {Array.from({ length: Math.min(3, uiStore.files.length + 1) }).map((_, index) => (
                <UploadFile
                  key={index}
                  contentUrl={uiStore.files[index]?.content ?? ''}
                  onChange={(file) => uiStore.setFiles([...uiStore.files, file])}
                  onDelete={() => deleteFileHandler(index)}
                  fileValidationOptions={ticketFileValidate}
                />
              ))}
            </Flex>
          </Form.Item>

          <Form.Item<{ message: string }>
            label={t(getActivateOrderPageKey('Сообщение оператору'))}
            name={'message'}
            rules={[{ min: 30, message: t(getSharedKey('minLength'), { count: 30 }) }]}
          >
            <Flex gap={8} align="center">
              <TextArea
                value={uiStore.message}
                onChange={(e) => uiStore.setMessage(e.target.value)}
                maxLength={500}
                placeholder={`${t(getSharedKey('enterMessage'))}...`}
              />

              <Button
                onClick={sendMessageButton}
                disabled={uiStore.message.length < 30}
                loading={loading}
                icon={<SendMessageIcon width="24" height="24" />}
                style={{ padding: 26 }}
              />
            </Flex>
          </Form.Item>
        </Form>
      )}
    </Flex>
  )
})
